/**
 * Global Snackbar – single snackbar component.
 * Subscribes to snackbar store, renders gradient-style banner (white card, left icon strip, title + message, close).
 * Auto-clears after 3s; close button dismisses immediately.
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  Animated,
  Pressable,
  Platform,
  Easing,
  type ViewStyle,
} from 'react-native';
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  X,
  type LucideIcon,
} from 'lucide-react-native';
import { useTheme } from '../../../theme/useTheme';
import {
  getSnackbarStyles,
  type SnackbarStyles,
} from './GlobalSnackbar.styles';
import { WEB_HOVER_TRANSITION_STYLE } from '../../constants/animations';
import { useSnackbarStore } from '../../stores/snackbarStore';
import type { SnackbarVariant } from '../../stores/snackbarStore';

const SNACKBAR_AUTO_HIDE_MS = 3000;

const VARIANT_ICONS: Record<SnackbarVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

const CONTAINER_KEYS: Record<SnackbarVariant, keyof SnackbarStyles> = {
  info: 'containerInfo',
  success: 'containerSuccess',
  warning: 'containerWarning',
  error: 'containerError',
};

const VARIANT_TITLES: Record<SnackbarVariant, string> = {
  error: 'Error',
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
};

const ICON_STRIP_KEYS: Record<SnackbarVariant, keyof SnackbarStyles> = {
  info: 'iconStripInfo',
  success: 'iconStripSuccess',
  warning: 'iconStripWarning',
  error: 'iconStripError',
};

const ENTER_DURATION = 280;
const EXIT_DURATION = 200;

/** RN Web: opacity/transform + useNativeDriver often leaves text/SVG unpainted (empty “bar”). */
const USE_NATIVE_DRIVER_FOR_SNACKBAR_ANIM = Platform.OS !== 'web';

function getVariantIconColor(
  theme: ReturnType<typeof useTheme>,
  variant: SnackbarVariant
): string {
  switch (variant) {
    case 'info':
      return theme.info;
    case 'success':
      return theme.success;
    case 'warning':
      return theme.warning;
    case 'error':
      return theme.error;
    default:
      return theme.error;
  }
}

export const GlobalSnackbar: React.FC = () => {
  const snackbar = useSnackbarStore((s) => s.snackbar);
  const clearSnackbar = useSnackbarStore((s) => s.clearSnackbar);
  const theme = useTheme();
  const styles = useMemo(() => getSnackbarStyles(theme), [theme]);

  const opacity = useRef(new Animated.Value(0)).current;
  const translateX = useRef(new Animated.Value(50)).current;
  const scale = useRef(new Animated.Value(0.9)).current;

  const runEnter = useCallback(() => {
    opacity.setValue(0);
    translateX.setValue(50);
    scale.setValue(0.9);
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: ENTER_DURATION,
        useNativeDriver: USE_NATIVE_DRIVER_FOR_SNACKBAR_ANIM,
        easing: Easing.out(Easing.cubic),
      }),
      Animated.timing(translateX, {
        toValue: 0,
        duration: ENTER_DURATION,
        useNativeDriver: USE_NATIVE_DRIVER_FOR_SNACKBAR_ANIM,
        easing: Easing.out(Easing.cubic),
      }),
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: USE_NATIVE_DRIVER_FOR_SNACKBAR_ANIM,
        tension: 260,
        friction: 20,
      }),
    ]).start();
  }, [opacity, translateX, scale]);

  const runExit = useCallback(
    (callback?: () => void) => {
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: 0,
          duration: EXIT_DURATION,
          useNativeDriver: USE_NATIVE_DRIVER_FOR_SNACKBAR_ANIM,
          easing: Easing.in(Easing.ease),
        }),
        Animated.timing(translateX, {
          toValue: 50,
          duration: EXIT_DURATION,
          useNativeDriver: USE_NATIVE_DRIVER_FOR_SNACKBAR_ANIM,
          easing: Easing.in(Easing.ease),
        }),
      ]).start(() => {
        callback?.();
      });
    },
    [opacity, translateX]
  );

  useEffect(() => {
    if (!snackbar) return;
    const durationMs = snackbar.durationMs ?? SNACKBAR_AUTO_HIDE_MS;
    const timeout = setTimeout(clearSnackbar, durationMs);
    return () => clearTimeout(timeout);
  }, [snackbar, clearSnackbar]);

  useEffect(() => {
    if (snackbar) runEnter();
  }, [snackbar, runEnter]);

  const handleClose = useCallback(() => {
    runExit(clearSnackbar);
  }, [clearSnackbar, runExit]);

  const handleActionPress = useCallback(() => {
    if (!snackbar?.action) return;
    snackbar.action.onPress();
    runExit(clearSnackbar);
  }, [snackbar, runExit, clearSnackbar]);

  if (!snackbar) return null;

  const { message, variant, title, action } = snackbar;
  const containerVariantStyle =
    (styles[CONTAINER_KEYS[variant]] ?? styles.containerError) as ViewStyle;
  const iconStripVariantStyle =
    (styles[ICON_STRIP_KEYS[variant]] ?? styles.iconStripError) as ViewStyle;
  const iconColor = getVariantIconColor(theme, variant);
  const IconComponent = VARIANT_ICONS[variant] ?? VARIANT_ICONS.error;
  const displayTitle =
    (title != null && title !== '' ? title : VARIANT_TITLES[variant]) ??
    VARIANT_TITLES.error;

  const snackbarTree = (
    <Animated.View
      style={[
        styles.container,
        containerVariantStyle,
        {
          opacity,
          transform: [{ translateX }, { scale }],
        },
      ]}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
    >
      <View style={[styles.iconStrip as ViewStyle, iconStripVariantStyle]}>
        <IconComponent size={20} color={iconColor} />
      </View>
      <View style={styles.content}>
        <Text style={styles.title}>{displayTitle}</Text>
        <Text style={styles.message}>{message}</Text>
      </View>
      {action ? (
        <Pressable
          onPress={handleActionPress}
          accessibilityRole="button"
          accessibilityLabel={action.label}
          style={({ pressed, hovered }) => [
            styles.actionWrap,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.actionPressed,
            hovered && Platform.OS === 'web' && styles.actionHover,
          ]}
        >
          <Text style={[styles.actionLabel, { color: iconColor }]}>{action.label}</Text>
        </Pressable>
      ) : null}
      <Pressable
        onPress={handleClose}
        accessibilityRole="button"
        accessibilityLabel="Dismiss"
        style={({ pressed, hovered }) => [
          styles.closeWrap,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.closePressed,
          hovered && Platform.OS === 'web' && styles.closeHover,
        ]}
      >
        <X size={16} color={theme.gray[500]} />
      </Pressable>
    </Animated.View>
  );

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const { createPortal } = require('react-dom') as typeof import('react-dom');
    return createPortal(snackbarTree, document.body);
  }

  return snackbarTree;
};
